import * as yup from 'yup';

import { Item, ItemDisplayUnit } from 'apis/restaurant-api/merchant-admin/menu2/types';

import {
  yupNoUnallowedCombination,
  yupBinaryInventory,
  yupTestBarcode,
  yupTestEitherInventoryMode,
  yupTestMissingWeightStep,
  yupTestMonetaryDecimals,
  yupTestProductHierarchyForIsOverTheCounter,
  yupTestUpToDecimalPlaces,
  yupTestCategoryMismatch,
  yupTestMultipleCategoryMatches,
  yupTestDietaryPreferences,
  yupTestMandatoryWarnings,
  yupTestDeposits,
} from './validationUtils';

const PRODUCT_HIERARCHY_HUMAN_READABLE_ID_LENGTH = 9;

export const csvValidationSchema = yup.object({
  item_id: yup.string().required(),
  gtin: yup
    .string()
    .transform(v => v.replace(/\D/g, ''))
    .test(yupTestBarcode),
  merchant_sku: yup.string().trim(),
  name: yup.string().required(),
  brand_id: yup.string(),
  caffeine_content_units: yup
    .mixed<NonNullable<Item['caffeine_content']>['units']>()
    .oneOf(['mg_per_100_g', 'mg_per_100_ml']),
  caffeine_content_value: yup.number().positive(),
  description: yup.string(),
  product_hierarchy_id: yup
    .string()
    .transform(v => v.padStart(PRODUCT_HIERARCHY_HUMAN_READABLE_ID_LENGTH, '0'))
    .test(yupTestProductHierarchyForIsOverTheCounter),
  product_hierarchy_name: yup.string(),
  price: yup.number().min(0).test(yupTestMonetaryDecimals).required(),
  discounted_price: yup
    .number()
    .min(0)
    .test(yupTestMonetaryDecimals)
    .when('price', (price: number | undefined, schema: yup.NumberSchema) =>
      price !== undefined ? schema.lessThan(price) : schema,
    )
    .nullable()
    .default(null),
  weight_in_grams: yup.number().positive(),
  volume_in_ml: yup.number().positive(),
  number_of_units: yup.number().positive(),
  display_unit: yup.mixed<ItemDisplayUnit>().oneOf(['units', 'volume', 'volume_100ml', 'weight', 'weight_100g']),
  weighted_item_sell_by: yup
    .mixed<'piece' | 'weight'>()
    .oneOf(['piece', 'weight'])
    .when(['weighted_item_approx_weight_in_grams', 'weighted_item_min_weight_in_grams'], {
      is: (
        weighted_item_approx_weight_in_grams: number | undefined,
        weighted_item_min_weight_in_grams: number | undefined,
      ) => weighted_item_approx_weight_in_grams || weighted_item_min_weight_in_grams,
      then: schema => schema.required({ id: 'validation.missingSellByOption' }),
    })
    .test(yupTestMissingWeightStep)
    .test(yupNoUnallowedCombination),
  weighted_item_approx_weight_in_grams: yup.number().positive(),
  weighted_item_min_weight_in_grams: yup.number().positive(),
  vat_percentage: yup.number().min(0).max(100).test(yupTestUpToDecimalPlaces(2)).required(),
  alcohol_percentage: yup.number().min(0).max(100).test(yupTestUpToDecimalPlaces(1)),
  pos_id: yup.string(),
  country_of_origin: yup.string(),
  ingredients: yup.string(),
  additives: yup.string(),
  nutrition_facts: yup.string(),
  allergens: yup.string(),
  conditions_of_use_and_storage: yup.string(),
  more_information: yup.string().max(5000),
  distributor_information: yup.string(),
  deposit_value: yup.number().positive().test(yupTestDeposits),
  deposit_vat: yup.number().min(0).max(100).test(yupTestUpToDecimalPlaces(2)).test(yupTestDeposits),
  producer_information: yup.string(),
  enabled: yup.mixed<'YES' | 'NO'>().oneOf(['YES', 'NO']).required(),
  is_over_the_counter: yup.mixed<'YES' | 'NO'>().oneOf(['YES', 'NO']).required(),
  min_quantity_per_purchase: yup
    .number()
    .positive()
    .integer()
    .when(
      ['weighted_item_sell_by', 'weighted_item_min_weight_in_grams'],
      (weighted_item_sell_by, weighted_item_min_weight_in_grams) =>
        weighted_item_sell_by === 'weight' && weighted_item_min_weight_in_grams !== undefined
          ? yup
              .number()
              .positive()
              .integer()
              .min(weighted_item_min_weight_in_grams, {
                id: 'validation.mustBeGreaterOrEqualThanMin',
                values: { min: 'weighted_item_min_weight_in_grams' },
              })
          : yup.number().positive().integer(),
    ),
  max_quantity_per_purchase: yup
    .number()
    .positive()
    .integer()
    .when(
      ['weighted_item_sell_by', 'weighted_item_min_weight_in_grams'],
      (weighted_item_sell_by, weighted_item_min_weight_in_grams) =>
        weighted_item_sell_by === 'weight' && weighted_item_min_weight_in_grams !== undefined
          ? yup
              .number()
              .positive()
              .integer()
              .min(weighted_item_min_weight_in_grams, {
                id: 'validation.mustBeGreaterOrEqualThanMin',
                values: { min: 'weighted_item_min_weight_in_grams' },
              })
              .when(['min_quantity_per_purchase'], min_quantity_per_purchase =>
                min_quantity_per_purchase !== undefined
                  ? yup.number().positive().integer().min(min_quantity_per_purchase)
                  : yup.number().positive().integer(),
              )
          : yup
              .number()
              .positive()
              .integer()
              .when(['min_quantity_per_purchase'], min_quantity_per_purchase =>
                min_quantity_per_purchase !== undefined
                  ? yup.number().positive().integer().min(min_quantity_per_purchase)
                  : yup.number().positive().integer(),
              ),
    ),
  use_limited_quantity: yup.mixed<'YES' | 'NO'>().oneOf(['YES', 'NO']),
  use_inventory_management: yup.mixed<'YES' | 'NO'>().oneOf(['YES', 'NO']).test(yupTestEitherInventoryMode),
  is_in_stock: yup.mixed<'YES' | 'NO'>().oneOf(['YES', 'NO']).test(yupBinaryInventory),
  is_age_restricted_item: yup.mixed<'YES' | 'NO'>().oneOf(['YES', 'NO']).required(),
  delivery_handshake: yup.mixed<'YES' | 'NO'>().oneOf(['YES', 'NO']).required(),
  age_limit: yup.number().positive().integer(),
  item_balance: yup.number().integer().min(0),
  location: yup.string(),
  commission_delivery: yup.number().min(0).max(100).test(yupTestUpToDecimalPlaces(2)),
  commission_pickup: yup.number().min(0).max(100).test(yupTestUpToDecimalPlaces(2)),
  category_id: yup.string(),
  category_name: yup.string().test(yupTestCategoryMismatch).test(yupTestMultipleCategoryMatches),
  category_item_sequence: yup.number().integer().positive(),
  picking_area: yup.string(),
  picking_sequence: yup.number().integer().positive(),
  dietary_preferences: yup.string().test(yupTestDietaryPreferences),
  mandatory_warnings: yup.string().test(yupTestMandatoryWarnings),
  image_url: yup.string(),
  return_policy: yup.string(),
});

export type ValidCSVData = Partial<yup.Asserts<typeof csvValidationSchema>>;
